// +----------------------------------------------------------------------
// | 首页：一个输入框，两种出口
// |   💬 聊聊（默认主推）→ 带着这句话进对话页，自动发出
// |   📋 分析 → 原决策流程（追问 / 30 秒 / 随便吧）
// | 2026-09-29 简化改版：快捷方块、领码/下载、统计全部移出首屏，
// | 首屏只做一件事 —— 让用户把纠结的事说出来。
// | 2026-09-30 领体验码 / 下载 App 入口彻底从首页移除（「我的」页已有），
// | 首页不再引 EntrySwitches（该组件仍供 Profile 的 plain 变体使用）。
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DecisionInput from '../../components/DecisionInput';
import ModeSwitch from '../../components/ModeSwitch';
import Thinking from '../../components/Thinking';
import { toast } from '../../components/Toast';
import { createDecision, listDecisions } from '../../api/decision';
import useAuth from '../../hooks/useAuth';
import useQuota from '../../hooks/useQuota';
import { categoryEmoji, categoryText, statusLabel } from '../../lib/categoryMap';
import { fromNow, greeting } from '../../lib/format';
import './Home.less';

// 不知道怎么写？点一下填进去（降低「不会提问」的门槛）
const SAMPLE_QUESTIONS = [
  '周末去爬山还是在家？',
  '这个相机要不要买？',
  '晚上吃火锅还是烧烤？',
  '这个项目我要不要接？',
];

/**
 * 想想的形象：圆头小机器人。
 * 纯内联 SVG + CSS 动画（待机浮动 + 周期性眨眼），零图片资源；
 * 用户对着它说话，比对着一个表单框倾诉感强得多。
 */
function HomeRobot() {
  return (
    <div className="home-robot" aria-hidden="true">
      <svg viewBox="0 0 96 96" width="46" height="46">
        {/* 天线 */}
        <line x1="48" y1="14" x2="48" y2="25" stroke="#E8A183" strokeWidth="3" strokeLinecap="round" />
        <circle cx="48" cy="10" r="4.5" fill="#F2603F" />
        {/* 头 */}
        <rect x="20" y="25" width="56" height="48" rx="22" fill="#FFFFFF" stroke="#F0DCD2" strokeWidth="2.5" />
        {/* 眼睛（眨眼动画作用在整个 g 上） */}
        <g className="home-robot-eyes">
          <circle cx="37" cy="47" r="4.2" fill="#3D322C" />
          <circle cx="59" cy="47" r="4.2" fill="#3D322C" />
        </g>
        {/* 腮红 */}
        <circle cx="29.5" cy="56" r="3.5" fill="#FFD9CC" opacity="0.9" />
        <circle cx="66.5" cy="56" r="3.5" fill="#FFD9CC" opacity="0.9" />
        {/* 微笑 */}
        <path d="M42 58 Q48 63.5 54 58" stroke="#3D322C" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export default function Home() {
  const [question, setQuestion] = useState('');
  // 出口：chat = 直接聊聊（默认主推）；form = 表单分析（原决策流程）
  const [channel, setChannel] = useState('chat');
  const [mode, setMode] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);

  const { isLoggedIn, user } = useAuth();
  // 「帮我想想」的每日额度（非会员 3 次 / 会员不限次）。
  // 额度只在登录后有意义，未登录时 useQuota 返回 null，不渲染计数器。
  const { quota, applyFrom } = useQuota('decision');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 从话题详情页带问题回来：/?ask=要不要换工作
  useEffect(() => {
    const ask = searchParams.get('ask');
    if (ask) {
      setQuestion(ask);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadRecent = useCallback(async () => {
    if (!isLoggedIn) {
      setRecent([]);
      return;
    }
    try {
      const data = await listDecisions({ page: 1, page_size: 3 });
      const list = (data && (data.list || data.items || data.data)) || [];
      setRecent(Array.isArray(list) ? list : []);
    } catch (e) {
      // 决策接口（Phase 3）还没就绪时静默处理，不打扰用户
      setRecent([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // 💬 聊聊出口：把这句话带进对话页，由对话页自动发出。
  //    聊天有自己的额度体系（会话接口自己拦 429），首页不预拦。
  const submitChat = (text) => {
    if (!isLoggedIn) {
      toast('先登录一下，我才好帮你记住这些纠结');
      navigate('/login', { state: { from: '/' } });
      return;
    }
    navigate('/chat', { state: { initial: text } });
  };

  // 📋 分析出口：原决策流程，逻辑一行没动
  const submitForm = async (text) => {
    if (submitting) return;

    if (!isLoggedIn) {
      toast('先登录一下，我才好帮你记住这些纠结');
      navigate('/login', { state: { from: '/' } });
      return;
    }

    // 额度用完就别发请求了：直接告诉用户和去哪开会员。
    // 服务端也会拦（那道闸门是权威的），这里提前拦只是为了少一次白跑的
    // 请求 + 少一次「转半天然后报错」的挫败感。
    if (quota && quota.unlimited === false && quota.exceeded) {
      toast('今天的免费次数用完了，明天再来', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const data = await createDecision({ question: text, mode });
      setQuestion('');
      // 后端口径：创建成功就扣一次。顺手把最新额度铺上，
      // 「今天还能问 N 次」立刻减 1，不用等下次进页面
      if (data && data.decision_quota) applyFrom(data.decision_quota);
      if (data && data.id) {
        navigate(`/decision/${data.id}`);
      }
    } catch (e) {
      // 429 = 额度用完。这是唯一需要「换一种语气」的错误：
      // 别的错误是「出错了重试」，这个是「今天到此为止，明天再来」。
      if (e.code === 429) {
        if (e.data && e.data.quota) applyFrom(e.data.quota);
        toast(e.message || '今天的免费次数用完了，明天再来', 'error');
        return;
      }
      toast(e.message || '没想出来，再试一次', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (text) => {
    if (channel === 'chat') {
      submitChat(text);
    } else {
      submitForm(text);
    }
  };

  const fillQuestion = (text) => {
    setQuestion(text);
  };

  return (
    <div className="page home">
      <section className="home-hero">
        <h1 className="home-title">想想</h1>
        <p className="home-slogan">有啥纠结的，扔过来。</p>
        {isLoggedIn && user && (
          <p className="home-greeting muted">
            {greeting()}
            {user.nickname ? `，${user.nickname}` : ''}
          </p>
        )}
      </section>

      {/* 输入控制台：拟人化形象 + 出口切换 + 输入框，首屏唯一的主角 */}
      <section className="home-input fade-in-up">
        <div className="home-console-head">
          <HomeRobot />
          <p className="home-console-hello">
            我在听
            <span className="home-console-hello-sub">说出来就行，不用组织语言</span>
          </p>
          <div className="home-console-tabs">
            <button
              type="button"
              className={`home-console-tab ${channel === 'chat' ? 'is-active' : ''}`}
              onClick={() => setChannel('chat')}
            >
              💬 聊聊
            </button>
            <button
              type="button"
              className={`home-console-tab ${channel === 'form' ? 'is-active' : ''}`}
              onClick={() => setChannel('form')}
            >
              📋 分析
            </button>
          </div>
        </div>

        <DecisionInput
          value={question}
          onChange={setQuestion}
          onSubmit={handleSubmit}
          loading={submitting}
          placeholder={channel === 'chat' ? '今天在纠结什么？说出来就行……' : '周末不知道去哪……'}
          buttonText={channel === 'chat' ? '发出去' : '帮我想想'}
        />

        {channel === 'chat' ? (
          <p className="home-mode-hint home-console-hint muted">
            像跟朋友发消息一样，说着说着就想清楚了
          </p>
        ) : (
          <div className="home-mode">
            <ModeSwitch value={mode} onChange={setMode} disabled={submitting} />
            <span className="home-mode-hint muted">
              {mode === 'quick' && '只问几个关键问题，快速给倾向'}
              {mode === 'normal' && '必要时我会先问你几个关键问题'}
              {mode === 'random' && '这是娱乐模式，不是理性决策'}
            </span>
          </div>
        )}

        {/* 剩余次数：只在登录后显示。
            会员什么都不显示 —— 「不限次」是给自己人看的状态，
            不是需要每天确认的信息，会员看到只剩噪音（2026-09-29 用户反馈）。
            用完了就把「开通会员」直接摆出来，别让用户自己去找入口。
            只跟「分析」出口有关（聊聊走聊天自己的额度），所以只在 form 显示 */}
        {channel === 'form' && isLoggedIn && quota && !quota.unlimited && (
          <p className={`home-quota${quota.exceeded ? ' is-out' : ''}`}>
            {quota.exceeded ? (
              <>
                <span>今天的 {quota.limit} 次用完了，明天再来</span>
                <Link to="/vip" className="home-quota-link">
                  开通会员不限次 ›
                </Link>
              </>
            ) : (
              <>
                <span>
                  今天还能问 <strong>{quota.remaining}</strong> 次
                </span>
                <Link to="/vip" className="home-quota-link muted">
                  会员不限次 ›
                </Link>
              </>
            )}
          </p>
        )}
      </section>

      <section className="home-samples">
        <span className="home-samples-label muted">不知道怎么问？点一下填进去</span>
        <div className="home-chips">
          {SAMPLE_QUESTIONS.map((text) => (
            <button key={text} type="button" className="home-chip" onClick={() => fillQuestion(text)}>
              {text}
            </button>
          ))}
        </div>
      </section>

      {/* 聊天页入口：输入框里的「聊聊」是一次性的，这里进的是对话列表 ——
          接着上次没聊完的继续。视觉沿用原来热门折叠行的行样式，低调不抢输入框 */}
      <section className="home-secondary">
        <Link to="/chat" className="home-chat-entry">
          <span className="home-chat-entry-emoji">💬</span>
          <span className="home-chat-entry-body">
            <strong>继续和想想聊</strong>
            <span className="muted">接着上次的话题，或者重新开一个</span>
          </span>
          <span className="home-chat-entry-arrow">›</span>
        </Link>
      </section>

      <section className="home-recent">
        <div className="home-card-head">
          <h3>最近的决定</h3>
          {isLoggedIn && recent.length > 0 && (
            <Link to="/history" className="muted">
              全部 ›
            </Link>
          )}
        </div>

        {recent.length > 0 ? (
          <ul className="home-recent-list">
            {recent.map((item) => (
              <li key={item.id}>
                <Link to={`/decision/${item.id}`} className="home-recent-item">
                  <span className="home-recent-emoji">{categoryEmoji(item.category)}</span>
                  <span className="home-recent-body">
                    <span className="home-recent-title">{item.title || item.question}</span>
                    <span className="muted">
                      {categoryText(item.category)}
                      {item.created_at ? ` · ${fromNow(item.created_at)}` : ''}
                      {item.status ? ` · ${statusLabel(item.status)}` : ''}
                    </span>
                  </span>
                  <span className="home-recent-arrow">›</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="home-empty">
            {isLoggedIn ? (
              <p className="muted">还没有记录。上面那件纠结的事，说给我听听？</p>
            ) : (
              <>
                <p className="muted">登录后，我做过的分析和你的决定都会存在这里。</p>
                <div className="home-empty-actions">
                  <Link to="/login" className="btn btn-primary btn-sm">
                    登录
                  </Link>
                  <Link to="/register" className="btn btn-ghost btn-sm">
                    注册
                  </Link>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      <footer className="home-footer muted">
        想想只帮你把事情想清楚，最后的决定永远是你的。
      </footer>

      {/* 提交分析期间的加载框：遮罩 + 转圈 + 轮换文案，锁滚动防连点焦虑 */}
      {submitting && <Thinking />}
    </div>
  );
}
